import { createAction } from 'redux-actions';
import { push } from 'react-router-redux';

import { organizeMrtData } from 'util/mrt';
import { wrapFetch } from 'actions/endpoint';
import { openFailedAlert } from 'actions/alert';
import { mapRoomType, normalizeRange, calculateDigit } from 'util/helper';
import { t } from 'util/i18n';

const calcEndMonth = (year, month) => {
	if (month === 11) {
		return { year: year + 1, month: 0 };
	}

	return { year, month: month + 1 };
};

const filterKeyMapRoute = {
	city: (value, query) => {
		const { district: _, ...resetQuery } = query;

		if (![1, 2, 3].includes(value.select[0].value)) {
			const { mrt_line: __, mrt: ___, ...resultQuery } = resetQuery;
			return { ...resultQuery, city: value.select[0].value };
		}

		return { ...resetQuery, city: value.select[0].value };
	},

	district: (value, query) => {
		if (value.select[0].value === -1) {
			const { district: _, ...resultQuery } = query;
			return resultQuery;
		}

		return { ...query, district: value.select[0].value };
	},

	month: (value, query) => {
		if (value.range.length < 2) {
			const { live_start: _, live_end: __, ...other } = query;
			return other;
		}

		const startYear = value.yearRange[0];
		const startMonth = value.range[0];

		const { year: endYear, month: endMonth } = calcEndMonth(value.yearRange[1], value.range[1]);

		return {
			...query,
			live_start: `${startYear}-${startMonth + 1}-01`,
			live_end: `${endYear}-${endMonth + 1}-01`,
		};
	},

	mrtLine: (value, query) => {
		const { mrt: _, ...resetQuery } = query;

		if (value.select[0].value === 0) {
			const { mrt_line: __, ...resultQuery } = resetQuery;
			return resultQuery;
		}

		return { ...resetQuery, mrt_line: value.select[0].value };
	},

	mrtStation: (value, query) => ({ ...query, mrt: value.select[0].value }),

	mrtAround: (value, query) => ({ ...query, near_station: value.value ? 1 : 0 }),

	apartment: (value, query) => {
		if (value.select[0].value === -1) {
			const { apartment_id: __, ...resultQuery } = query;
			return resultQuery;
		}

		return { ...query, apartment_id: value.select[0].value };
	},

	roomType: (value, query) => ({ ...query, room_type: value.select.map(item => item.value) }),

	price: (value, query) => ({ ...query, price_start: value.range[0], price_end: value.range[1] }),

	area: (value, query) => ({ ...query, ping_start: value.range[0], ping_end: value.range[1] }),

	double: (value, query) => {
		if (value.select.value === '-1') {
			// remove unnecessary query
			const { double: _, ...other } = query;
			return other;
		}

		return { ...query, double: value.select.value };
	},

	pet: (value, query) => {
		if (value.select.value === '-1') {
			// remove unnecessary query
			const { pet: _, ...other } = query;
			return other;
		}

		return { ...query, pet: value.select.value };
	},

	feature: (value, query) => ({ ...query, tag: value.select.map(item => item.value) }),

	unavailableType: (value, query) => ({ ...query, type: value.value ? 'all' : 'waiting' }),

	sort: (value, query) => ({ ...query, sort_by: value.select[0].value }),
};

export const updateRoomFilter = createAction('UPDATE_ROOM_FILTER', (key, value) => ({
	key,
	value,
}));

export const handleCityChangeEpic = action$ =>
	action$
		.ofType('UPDATE_ROOM_FILTER')
		.filter(action => action.payload.key === 'city')
		.map(action => ({
			type: 'UPDATE_DISTRICT_OPTIONS',
			payload: {
				list: action.payload.value.select[0].districts,
			},
		}));

export const handleMrtLineChangeEpic = action$ =>
	action$
		.ofType('UPDATE_ROOM_FILTER')
		.filter(action => action.payload.key === 'mrtLine')
		.map(action => ({
			type: 'UPDATE_MRT_STATION_OPTIONS',
			payload: { list: action.payload.value.select[0].stations },
		}));

export const handleQueryStringEpic = (action$, store) =>
	action$
		.ofType('UPDATE_ROOM_FILTER')
		.debounceTime(500)
		.map(action => {
			const { routing: { locationBeforeTransitions: { query, pathname } } } = store.getState();

			const newQuery = filterKeyMapRoute[action.payload.key](action.payload.value, query);

			// Prevent refresh the route if query not change
			if (newQuery === query) {
				return { type: 'FILTER_NOT_CHANGE' };
			}

			return push({
				pathname,
				query: newQuery,
			});
		});

export const getRoomFilter = createAction('FETCH_ROOM_FILTER', async () => {
	const { status, message, data } = await wrapFetch('room/getFilter', {
		method: 'GET',
	});

	if (status !== 200) {
		throw openFailedAlert('generic', message);
	}

	const cityData = {};
	const districtsData = { '-1': { value: -1, label: t('roomFilter:all') } };
	const cityList = data.city.map(item => {
		cityData[item.id] = { value: item.id, label: item.name };
		if (item.id !== 1) {
			cityData[item.id].districts = [
				-1,
				...item.districts.map(district => {
					districtsData[district.id] = { value: district.id, label: district.name };
					return district.id;
				}),
			];
		} else {
			cityData[item.id].districts = item.districts.map(district => {
				districtsData[district.id] = { value: district.id, label: district.name };
				return district.id;
			});
		}
		return item.id;
	});

	const apartmentData = { '-1': { value: -1, label: t('roomFilter:all') } };
	const apartmentList = [
		-1,
		...data.apartment.map(item => {
			apartmentData[item.id] = { value: item.id, label: item.name };

			return item.id;
		}),
	];

	const roomTypeData = {};
	const roomTypeList = data.room_type.map(item => {
		roomTypeData[item] = { value: item, label: mapRoomType(item) };

		return item;
	});

	const featureData = {};
	const featureList = data.apartment_tags.map(item => {
		featureData[item.id] = { value: item.id, label: item.name };

		return item.id;
	});

	return {
		...organizeMrtData(data.mrt),
		cityData,
		cityList,
		districtsData,
		apartmentData,
		apartmentList,
		roomTypeData,
		roomTypeList,
		minMaxPriceRange: normalizeRange(
			[data.min_price, data.max_price],
			calculateDigit(data.min_price) - 1,
		),
		minMaxAreaRange: normalizeRange(
			[data.min_ping, data.max_ping],
			calculateDigit(data.min_ping) - 1,
		),
		featureData,
		featureList,
	};
});

const normalizeMonth = (start, end) => {
	if (start.length < 3 || end.length < 3) {
		return { range: [], yearRange: [] };
	}

	const startMonth = parseInt(start[1], 10);
	const startYear = parseInt(start[0], 10);
	const endMonth = parseInt(end[1], 10);
	const endYear = parseInt(end[0], 10);

	if (startMonth > 12 || startMonth < 1 || endMonth > 12 || endMonth < 1) {
		return { range: [], yearRange: [] };
	}

	if (endMonth === 1) {
		return { range: [startMonth - 1, 11], yearRange: [startYear, endYear - 1] };
	}

	return { range: [startMonth - 1, endMonth - 2], yearRange: [startYear, endYear] };
};

export const setRoomFilterQuery = createAction('SET_ROOM_FILTER_QUERY', query => (_, getState) => {
	const resultFilter = {
		city: { select: [] },
		district: { select: [], list: [] },
		mrtLine: { select: [] },
		mrtStation: { select: [], list: [] },
		mrtAround: { value: false },
		apartment: { select: [] },
		roomType: { select: [] },
		month: { range: [], yearRange: [] },
		price: {},
		area: {},
		double: { select: { label: '', value: '' } },
		pet: { select: { label: '', value: '' } },
		feature: { select: [] },
		unavailableType: { value: false },
		sort: { select: [{ label: t('rooms:UpToDate'), value: 'new' }] },
	};

	if (query.city) {
		const { filter: { city } } = getState();
		const [cityId] = city.list.filter(id => id === parseInt(query.city, 10));

		if (cityId) {
			resultFilter.city.select = [city.data[cityId]];
			resultFilter.district.list = city.data[cityId].districts;
		}
	}

	if (query.district) {
		const { filter: { district } } = getState();
		const [districtId] = resultFilter.district.list.filter(
			id => id === parseInt(query.district, 10),
		);

		if (districtId) {
			resultFilter.district.select = [district.data[districtId]];
		}
	}

	if (query.mrt_line) {
		const { filter: { mrtLine } } = getState();
		const [mrtLineId] = mrtLine.list.filter(id => id === parseInt(query.mrt_line, 10));

		if (mrtLineId) {
			resultFilter.mrtLine.select = [mrtLine.data[mrtLineId]];
			resultFilter.mrtStation.list = mrtLine.data[mrtLineId].stations;
		}
	}

	if (query.mrt) {
		const { filter: { mrtStation } } = getState();
		const [mrtStationId] = resultFilter.mrtStation.list.filter(
			id => id === parseInt(query.mrt, 10),
		);

		if (mrtStationId) {
			resultFilter.mrtStation.select = [mrtStation.data[mrtStationId]];
		}
	}

	if (typeof query.near_station !== 'undefined') {
		resultFilter.mrtAround.value = query.near_station !== 0;
	}

	if (query.apartment_id) {
		const { filter: { apartment } } = getState();
		const [apartmentId] = apartment.list.filter(id => id === parseInt(query.apartment_id, 10));

		if (apartmentId) {
			resultFilter.apartment.select = [apartment.data[apartmentId]];
		}
	}

	if (query.room_type) {
		const { filter: { roomType } } = getState();
		const roomTypeIds = roomType.list.filter(type => query.room_type.includes(type));

		if (roomTypeIds.length > 0) {
			resultFilter.roomType.select = roomTypeIds.map(type => roomType.data[type]);
		}
	}

	if (query.live_start && query.live_end) {
		const liveStart = query.live_start.split('-');
		const liveEnd = query.live_end.split('-');

		const resultRange = normalizeMonth(liveStart, liveEnd);

		resultFilter.month.range = resultRange.range;
		resultFilter.month.yearRange = resultRange.yearRange;
	}

	if (query.price_start && query.price_end) {
		resultFilter.price.range = [parseInt(query.price_start, 10), parseInt(query.price_end, 10)];
	}

	if (query.ping_start && query.ping_end) {
		resultFilter.area.range = [parseInt(query.ping_start, 10), parseInt(query.ping_end, 10)];
	}

	if (typeof query.double !== 'undefined') {
		resultFilter.double.select = {
			value: query.double === '1' ? '1' : '0',
			label: query.double === '1' ? t('roomFilter:allowed') : t('roomFilter:notAllow'),
		};
	}

	if (typeof query.pet !== 'undefined') {
		resultFilter.pet.select = {
			value: query.pet === '1' ? '1' : '0',
			label: query.pet === '1' ? t('roomFilter:allowed') : t('roomFilter:notAllow'),
		};
	}

	if (query.tag) {
		const { filter: { feature } } = getState();
		const featureIds = feature.list.filter(type => query.tag.includes(`${type}`));

		if (featureIds.length > 0) {
			resultFilter.feature.select = featureIds.map(type => feature.data[type]);
		}
	}

	if (typeof query.type !== 'undefined') {
		resultFilter.unavailableType.value = query.type === 'all';
	}

	if (query.sort_by) {
		const sortOptions = [
			{ value: 'new', label: t('rooms:UpToDate') },
			{ value: 'high', label: t('rooms:HighestPrice') },
			{ value: 'low', label: t('rooms:LowestPrice') },
		];
		const matchSort = sortOptions.filter(item => item.value === query.sort_by);

		if (matchSort.length > 0) {
			resultFilter.sort.select = matchSort;
		}
	}

	return resultFilter;
});



// WEBPACK FOOTER //
// ./src/actions/filter.js